import { type ComponentProps, useCallback, useState } from 'react';
import { Color, Container, FormField, Group, Icon, IconKey, SecuredTextField, Text } from '@melio/penny';
import { PASSWORD_RULES, PASSWORD_RULES_ATTRIBUTE, isPasswordValid } from './passwordRules';

type IconColor = NonNullable<ComponentProps<typeof Icon>['color']>;

export { PASSWORD_RULES, isPasswordValid };

// Password Rule Item Component
interface PasswordRuleItemProps {
  isValid: boolean;
  label: string;
  showInvalidIcon?: boolean;
  testId?: string;
}

const getIcon = (isValid: boolean, showInvalidIcon: boolean) => {
  if (isValid) {
    return { iconType: 'checked' as IconKey, iconColor: 'success' as IconColor, textColor: 'semantic.text.success.rest' as Color };
  }
  if (showInvalidIcon) {
    return { iconType: 'close-mini' as IconKey, iconColor: 'critical' as IconColor, textColor: 'semantic.text.critical.rest' as Color };
  }
  return { iconType: 'checked' as IconKey, iconColor: 'inherit' as IconColor, textColor: 'global.neutral.700' as Color };
};

const PasswordRuleItem = ({ isValid, label, showInvalidIcon = true, testId }: PasswordRuleItemProps) => {
  const { iconType, iconColor, textColor } = getIcon(isValid, showInvalidIcon);

  return (
    <Group variant="horizontal" spacing="xs" alignItems="center" data-testid={testId}>
      <Icon type={iconType} size="small" color={iconColor} data-testid={testId ? `${testId}-icon` : undefined} />
      <Text textStyle="body4" color={textColor} data-testid={testId ? `${testId}-text` : undefined}>
        {label}
      </Text>
    </Group>
  );
};

export interface PasswordFieldProps {
  label: string;
  fieldProps: Record<string, unknown>;
  testId?: string;
  showRules?: boolean;
  showInvalidIcon?: boolean;
  autoComplete?: string;
  size?: 'small' | 'large';
}

export const PasswordField = ({
  label,
  fieldProps,
  testId = 'password-input',
  showRules = true,
  showInvalidIcon = true,
  autoComplete,
  size = 'large',
}: PasswordFieldProps) => {
  const [password, setPassword] = useState('');
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    node?.querySelector('input')?.setAttribute('passwordrules', PASSWORD_RULES_ATTRIBUTE);
  }, []);

  // Check if password rules should be shown (when password field has been interacted with)
  const showPasswordRules = (showRules && (isPasswordFocused || password.length > 0)) || showInvalidIcon;

  const isPasswordInvalid = !isPasswordValid(password);

  return (
    <Group variant="vertical">
      <Container
        ref={containerRef}
        onInput={(e: React.FormEvent<HTMLDivElement>) => {
          const input = e.target as HTMLInputElement;
          if (input.type === 'password' || input.type === 'text') {
            setPassword(input.value);
          }
        }}
        overflow="visible"
        onFocus={() => setIsPasswordFocused(true)}
      >
        <FormField
          {...fieldProps}
          labelProps={{ label }}
          helperText={undefined}
          isRequired
          size={size}
          render={(props) => (
            <SecuredTextField
              {...props}
              type="password"
              isInvalid={showInvalidIcon && isPasswordInvalid}
              placeholder=""
              {...(autoComplete !== undefined && { autoComplete })}
              data-testid={testId}
              data-private
            />
          )}
        />
      </Container>

      {/* Password Rules */}
      {showPasswordRules && (
        <Group variant="vertical" spacing="xs" data-testid="password-rules">
          <PasswordRuleItem
            isValid={PASSWORD_RULES.length.test(password)}
            label={PASSWORD_RULES.length.label}
            showInvalidIcon={showInvalidIcon}
            testId="password-rule-length"
          />
          <PasswordRuleItem
            isValid={PASSWORD_RULES.lettersAndNumber.test(password)}
            label={PASSWORD_RULES.lettersAndNumber.label}
            showInvalidIcon={showInvalidIcon}
            testId="password-rule-letters-and-number"
          />
          <PasswordRuleItem
            isValid={PASSWORD_RULES.specialChar.test(password)}
            label={PASSWORD_RULES.specialChar.label}
            showInvalidIcon={showInvalidIcon}
            testId="password-rule-special-char"
          />
        </Group>
      )}
    </Group>
  );
};
